import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import {
  Button,
  Flex,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Box,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Collapse
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import './model.css';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { PopupLayout } from "../../../../PopupLayout/PopupLayout";

const POPUP_POSITION_KEY = "popup-position-analysis-results";

// Hierarchical structure (with collapsible sections)
const hierarchicalSections = [
  {
    id: 'years',
    title: 'Years',
    fields: ['yearsValue'],
  },
  {
    id: 'interestRate',
    title: 'Interest Rate',
    fields: ['interestRateValue'],
  },
  {
    id: 'elecCost',
    title: 'Electricity Price',
    fields: ['elecCostValue'],
  },
  {
    id: 'NPV',
    title: 'NPV',
    fields: ['NPVValue']
  },
  {
    id: 'keyProductStream',
    title: 'Key Product Stream',
    fields: ['keyProductStreamValue']
  },
  {
    id: 'LCOP',
    title: 'LCOP',
    fields: ['LCOPValue']
  },
  {
    id: 'productRevenueAnnual',
    title: 'Annual Product Revenue',
    fields: ['totalProductRevValue']
  },
  {
    id: 'AdditionalRevenueAnnual',
    title: 'Additional Annual Revenue',
    fields: ['additionalRevenueAnnualValue']

  },
  // Capex section (with children)
  {
    id: 'capex',
    title: 'Total Capex Installed',
    isOpen: false,
    fields: ['totalInstalledValue'],
    children: [
      {
        id: 'equipmentCapex',
        title: 'Equipment Capex',
        isOpen: false,
        fields: ['equipmentCapexValue']
      },
      {
        id: 'installationCosts',
        title: 'Installation & Site Costs',
        isOpen: false,
        valueField: 'installationCostsValue',
        fields: [],
        children: [
          {
            id: 'langFactor',
            title: 'Lang Factor',
            isOpen: false,
            fields: ['langFactorValue']
          },
          {
            id: 'materialFactor',
            title: 'Material Factor',
            isOpen: false,
            fields: ['materialFactorValue']
          },
          {
            id: 'locationFactor',
            title: 'Location Factor',
            isOpen: false,
            fields: ['locationFactorValue']
          }
        ]
      },
      {
        id: 'additionalCapex',
        title: 'Additional Capex',
        isOpen: false,
        fields: ['additionalCapexValue']
      }
    ]
  },
  // Opex section (with children)
  {
    id: 'opex',
    title: 'Total Opex',
    isOpen: false,
    fields: ['totalOpexDisplayValue'],
    children: [
      {
        id: 'totalFeedstockCostAnnual',
        title: 'Total Feedstock Cost',
        isOpen: false,
        fields: ['totalFeedstockCostValue']
      },
      {
        id: 'utility',
        title: 'Utility',
        isOpen: false,
        fields: ['utilityValue']
      },
      {
        id: 'labor',
        title: 'Labor',
        isOpen: false,
        fields: ['laborValue'],
        children: [
          {
            id: 'operatorCount',
            title: 'Operator count',
            isOpen: false,
            fields: ['operatorCountValue']
          },
          {
            id: 'operatorSalary',
            title: 'Operator salary',
            isOpen: false,
            fields: ['operatorSalaryValue']
          },
          {
            id: 'operatorBenefits',
            title: 'Operator benefits',
            isOpen: false,
            fields: ['operatorBenefitsValue']
          },
          {
            id: 'benefitsPercent',
            title: 'Benefits %',
            isOpen: false,
            fields: ['benefitsPercentValue']
          },
          {
            id: 'laborOverheads',
            title: 'Labour overheads',
            isOpen: false,
            fields: ['laborOverheadsValue']
          }
        ]
      },
      {
        id: 'maintenance',
        title: 'Maintenance',
        isOpen: false,
        fields: ['maintenanceValue'],
        children: [
          {
            id: 'maintenanceFactor',
            title: 'Maintenance factor',
            isOpen: false,
            fields: ['maintenanceFactorValue']
          }
        ]
      },
      {
        id: 'landRent',
        title: 'Land Rent & Property Tax',
        isOpen: false,
        fields: ['landRentandPropertyTaxValue'],
        children: [
          {
            id: 'landRentFactor',
            title: 'Land Rent factor',
            isOpen: false,
            fields: ['landRentFactorValue']
          },
          {
            id: 'propertyTaxFactor',
            title: 'Property tax factor',
            isOpen: false,
            fields: ['propertyTaxFactorValue']
          }
        ]
      },
      {
        id: 'envCharges',
        title: 'Environmental Charges',
        isOpen: false,
        fields: ['envChargesValue'],
        children: [
          {
            id: 'envChargesFactor',
            title: 'Environmental charges factor',
            isOpen: false,
            fields: ['envChargesFactorValue']
          }
        ]
      },
      {
        id: 'additionalOpex',
        title: 'Additional Opex',
        isOpen: false,
        fields: ['additionalOpexValue']
      }
    ]
  }
];

// Build initial expansion state from hierarchicalSections
const buildInitialExpandState = (sections) => {
  const state = {};
  sections.forEach(section => {
    state[section.id] = { 
      isOpen: section.isOpen || false,
      children: {}
    };
    if (section.children && section.children.length > 0) {
      section.children.forEach(subsection => {
        state[section.id].children[subsection.id] = {
          isOpen: subsection.isOpen || false,
          children: {}
        };
        if (subsection.children && subsection.children.length > 0) {
          subsection.children.forEach(item => {
            state[section.id].children[subsection.id].children[item.id] = {
              isOpen: item.isOpen || false
            };
          });
        }
      });
    }
  });
  return state;
};

const PropertyPopup = forwardRef((props, ref) => {
  console.log(props.Id);
  const { isAuthenticated } = useAuth0();
  const componentRef = useRef(null);
  const cancelRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Define state for all fields
  const [state, setState] = useState({
    showingDialog: false,
    keyProductStreamValue: "",
    interestRateValue: "",
    yearsValue: "",
    NPVValue: "",
    LCOPValue: "",
    elecCostValue: "",
    additionalOpexValue: "",
    additionalCapexValue: "",
    totalOpexValue: "",
    totalOpexDisplayValue: "",
    totalCapexValue: "",
    totalInstalledValue: "",
    totalProductRevValue: "",
    additionalRevenueAnnualValue: "",
    totalFeedstockCostValue: "",
    // New fields for children in capex/opex (if needed)
    equipmentCapexValue: "",
    installationCostsValue: "",
    langFactorValue: "",
    materialFactorValue: "",
    locationFactorValue: "",
    utilityValue: "",
    laborValue: "",
    operatorCountValue: "",
    operatorSalaryValue: "",
    operatorBenefitsValue: "",
    benefitsPercentValue: "",
    laborOverheadsValue: "",
    maintenanceValue: "",
    maintenanceFactorValue: "",
    landRentandPropertyTaxValue: "",
    landRentFactorValue: "",
    propertyTaxFactorValue: "",
    wasteDisposalValue: "",
    envChargesFactorValue: "",
    envChargesValue: ""
  });

  


  // Allowed variables and mapping for updating econVars
  const allowedVars = [
    'keyProductStreamValue',
    'interestRateValue',
    'yearsValue',
    'elecCostValue',
    'additionalOpexValue',
    'additionalCapexValue',
    'langFactorValue',
    'materialFactorValue',
    'locationFactorValue' ,
    'operatorCountValue',
    'operatorSalaryValue',
    'operatorBenefitsValue',
    'benefitsPercentValue',
    'laborOverheadsValue',
    'maintenanceFactorValue',
    'landRentFactorValue',
    'propertyTaxFactorValue',
    'additionalRevenueAnnualValue',
    'envChargesFactorValue'
    // Add others if you want them to be editable
  ];

  const mapping = {
    'keyProductStreamValue': 'keyProductStream',
    'interestRateValue': 'interestRate',
    'yearsValue': 'years',
    'elecCostValue': 'elecCost',
    'additionalOpexValue': 'additionalOpex',
    'additionalCapexValue': 'additionalCapex',
    'langFactorValue': 'langFactor',
    'materialFactorValue': 'materialFactor',
    'locationFactorValue': 'locationFactor',
    'operatorCountValue': 'operatorCount',
    'operatorSalaryValue': 'operatorSalary',
    'operatorBenefitsValue': 'operatorBenefitsBase',
    'benefitsPercentValue': 'benefitsPercent',
    'laborOverheadsValue': 'laborOverheadFactor',
    'maintenanceFactorValue': 'maintenanceRate',
    'landRentFactorValue': 'landRent',
    'propertyTaxFactorValue': 'propertyTaxInsurance',
    'additionalRevenueAnnualValue': 'additionalRevenueAnnual',
    'envChargesFactorValue': 'envCharges'
  };

  const [dirtyFields, setDirtyFields] = useState({
    keyProductStreamValue: false,
    interestRateValue: false,
    yearsValue: false,
    elecCostValue: false,
    additionalOpexValue: false,
    additionalCapexValue: false
  });

  // Validation rules
  const validations = {
    'yearsValue': { min: 2, message: 'Years must be at least 2' },
    'interestRateValue': { min: 0, message: 'Interest rate must be at least 0' },
    'elecCostValue': { min: 0, message: 'Electricity cost must be at least 0' },
    'keyProductStreamValue': { min: 0, message: 'Key Product Stream must be specified' },
    'additionalRevenueAnnualValue': { min: 0, message: 'Additional Annual Revenue must be at least 0' } ,
    'envChargesFactorValue': { min: 0, message: 'Environmental charges factor must be at least 0' }
  };

  // Collapse/expand state for hierarchical sections
  const [expandState, setExpandState] = useState(buildInitialExpandState(hierarchicalSections));

  // Refs for uncontrolled inputs. This object stores a ref for each field.
  const inputRefs = useRef({});
  const getInputRef = (name) => {
    if (!inputRefs.current[name]) {
      inputRefs.current[name] = React.createRef();
    }
    return inputRefs.current[name];
  };

  // Uncontrolled input handlers: onBlur and onKeyDown.
  const handleBlur = (fieldName) => {
    const currentInput = getInputRef(fieldName).current;
    if (currentInput) {
      const rawValue = parseInputValue(currentInput.value);
      setState(prev => ({ ...prev, [fieldName]: rawValue }));
      setDirtyFields(prev => ({ ...prev, [fieldName]: true }));
    }
  };

  const handleKeyDown = async (event, fieldName) => {
    if (event.key === 'Enter') {
      const currentInput = getInputRef(fieldName).current;
      if (currentInput) {
        const rawValue = parseInputValue(currentInput.value);
        // Validate for allowed variables
        if (allowedVars.includes(fieldName)) {
          if (rawValue === '' || isNaN(parseFloat(rawValue))) {
            toast.error(`${fieldName.replace(/Value$/, '')} must be a valid number`);
            return;
          }
          if (validations[fieldName]) {
            const numValue = parseFloat(rawValue);
            if (numValue < validations[fieldName].min) {
              toast.error(validations[fieldName].message);
              return;
            }
          }
        }
        
        // Create a new state object with the updated field
        const newState = { ...state, [fieldName]: rawValue };
        setState(newState);
        
        // Immediately update props.econVars with the new value.
        // This ensures doReq sees the updated value.
        if (allowedVars.includes(fieldName) && mapping[fieldName]) {
          props.econVars[mapping[fieldName]] = rawValue === "" ? "" : parseFloat(rawValue);
          setDirtyFields(prev => ({ ...prev, [fieldName]: false }));
        }
        
        // Now run your calculations with the updated econVars.
        await doReq();
        
        // Remove focus from the input
        currentInput.blur();
      }
    }
  };
  

  // Parsing and formatting helpers
  const parseInputValue = (value) => {
    const cleanValue = value.replace(/,/g, '');
    return cleanValue === "" ? "" : cleanValue;
  };

  const formatDisplayNumber = (value) => {
    if (value === "" || value === null || isNaN(value)) return "";
    return parseFloat(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Update econVars for dirty fields
  const updateEconVars = () => {
    allowedVars.forEach(key => {
      if (mapping[key] && dirtyFields[key]) {
        const rawValue = state[key];
        props.econVars[mapping[key]] = rawValue === "" ? "" : parseFloat(rawValue);
        setDirtyFields(prev => ({ ...prev, [key]: false }));
      }
    });
  };

  // useEffect to run doReq on mount/auth change
  useEffect(() => {
    doReq();
    props.setGetEconArraysF(getEconArrays());
  }, [isAuthenticated]);

  const doReq = async () => {
    try {
      let [totalOpex, totalCapex, productCostSum, feedCostSum, capexSum] = await props.simulateEconVars(props.graph);
      let econVars = props.econVars;

      setState(prev => ({
        ...prev,
        keyProductStreamValue: econVars.keyProductStream || "",
        interestRateValue: econVars.interestRate || "",
        yearsValue: econVars.years || "",
        NPVValue: econVars.npvCalculated || "",
        LCOPValue: econVars.LCOP || "",
        elecCostValue: econVars.elecCost || "",
        //additionalOpexValue: econVars.additionalOpex || "",
        additionalOpexValue: (econVars.additionalOpex !== null && econVars.additionalOpex!== undefined)
        ? Number(econVars.additionalOpex).toFixed(2) || ""
        : "",
        //additionalCapexValue: econVars.additionalCapex || "",
        additionalCapexValue: (econVars.additionalCapex !== null && econVars.additionalCapex !== undefined)
        ? Number(econVars.additionalCapex).toFixed(2) || ""
        : "",
        additionalRevenueAnnualValue: (econVars.additionalRevenueAnnual !== null && econVars.additionalRevenueAnnual !== undefined)
        ? Number(econVars.additionalRevenueAnnual).toFixed(2) || ""
        : "",
        totalOpexValue: (econVars.totalOpex !== null && econVars.totalOpex !== undefined)
        ? Number(econVars.totalOpex).toFixed(2) || ""
        : "",
        totalOpexDisplayValue: (econVars.totalOpexDisplay !== null && econVars.totalOpexDisplay !== undefined)
        ? Number(econVars.totalOpexDisplay).toFixed(2) || ""
        : "",
        totalCapexValue: capexSum || "",
        totalInstalledValue: totalCapex || "",
        maintenanceFactorValue: (econVars.maintenanceRate !== null && econVars.maintenanceRate !== undefined)
        ? Number (econVars.maintenanceRate).toFixed(2) || ""
        :"",
        landRentFactorValue: (econVars.landRent !== null && econVars.landRent !== undefined)
        ? Number (econVars.landRent).toFixed(2) || ""
        :"",
        propertyTaxFactorValue: (econVars.propertyTaxInsurance !== null && econVars.propertyTaxInsurance !== undefined)
        ? Number(econVars.propertyTaxInsurance).toFixed(2) || ""
        :"",
        langFactorValue: (econVars.langFactor !== null && econVars.langFactor !== undefined)
        ? Number(econVars.langFactor).toFixed(2) || ""
        :"",
        materialFactorValue: (econVars.materialFactor !== null && econVars.materialFactor !== undefined)
        ? Number(econVars.materialFactor).toFixed(2) || ""
        :"",
        locationFactorValue: (econVars.locationFactor !== null && econVars.locationFactor !== undefined)
        ? Number(econVars.locationFactor).toFixed(2) || ""
        :"",
        operatorCountValue: (econVars.operatorCount !== null && econVars.operatorCount !== undefined) 
        ? Number(econVars.operatorCount).toFixed(2)
        : "",
        operatorSalaryValue: (econVars.operatorSalary !== null && econVars.operatorSalary !== undefined)
        ? Number(econVars.operatorSalary).toFixed(2)
        : "",
        operatorBenefitsValue: (econVars.operatorBenefitsBase !== null && econVars.operatorBenefitsBase !== undefined)
        ? Number(econVars.operatorBenefitsBase).toFixed(2)
        : "",
        benefitsPercentValue: (econVars.benefitsPercent !== null && econVars.benefitsPercent !== undefined)
        ? Number(econVars.benefitsPercent).toFixed(2)
        : "",
        laborOverheadsValue: (econVars.laborOverheadFactor !== null && econVars.laborOverheadFactor !== undefined)
        ? Number(econVars.laborOverheadFactor).toFixed(2)
        : "",
        installationCostsValue: (econVars.installationCosts !== null && econVars.installationCosts !== undefined)
        ? Number(econVars.installationCosts).toFixed(2)
        : "",
        envChargesValue: (econVars.environmentalCosts !== null && econVars.environmentalCosts !== undefined)
        ? Number(econVars.environmentalCosts).toFixed(2)
        : "",
        envChargesFactorValue: (econVars.envCharges !== null && econVars.envCharges !== undefined)
        ? Number(econVars.envCharges).toFixed(2)
        : "",
        landRentandPropertyTaxValue: (econVars.landRentandPropertyTax !== null && econVars.landRentandPropertyTax !== undefined)
        ? Number(econVars.landRentandPropertyTax).toFixed(2)
        : "",
        maintenanceValue: (econVars.maintenanceCosts !== null && econVars.maintenanceCosts !== undefined)
        ? Number(econVars.maintenanceCosts).toFixed(2)
        : "",
        laborValue: (econVars.laborCosts !== null && econVars.laborCosts !== undefined)
        ? Number(econVars.laborCosts).toFixed(2)
        : "",
        equipmentCapexValue: (econVars.equipmentCapex !== null && econVars.equipmentCapex !== undefined)
        ? Number(econVars.equipmentCapex).toFixed(2)
        : "",
        totalProductRevValue: (econVars.productRevAnnual !== null && econVars.productRevAnnual !== undefined)
        ? Number(econVars.productRevAnnual).toFixed(2)
        : "",
        totalFeedstockCostValue: (econVars.feedCostSumAnnual !== null && econVars.feedCostSumAnnual !== undefined)
        ? Number(econVars.feedCostSumAnnual).toFixed(2)
        : "",
        utilityValue: (econVars.utilityCosts !== null && econVars.utilityCosts !== undefined)
        ? Number(econVars.utilityCosts).toFixed(2)
        : "",

        // Additional fields for nested sections if provided...
      }));
    } catch (error) {
      console.warn("Error in doReq:", error);
      toast?.error?.("Failed to update financial calculations");
    }
  };

  // Collapse toggle functions for each level
  const toggleLevel1 = (sectionId) => {
    setExpandState(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], isOpen: !prev[sectionId].isOpen }
    }));
  };

  const toggleLevel2 = (sectionId, subsectionId) => {
    setExpandState(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        children: {
          ...prev[sectionId].children,
          [subsectionId]: {
            ...prev[sectionId].children[subsectionId],
            isOpen: !prev[sectionId].children[subsectionId].isOpen
          }
        }
      }
    }));
  };

  // Imperative handle for closing the popup (check dirty flags)
  useImperativeHandle(ref, () => ({
    canClose: () => {
      const hasDirty = Object.values(dirtyFields).some(isDirty => isDirty);
      if (hasDirty) {
        onOpen();
        return false;
      }
      return true;
    }
  }));

  // HierarchyRow: renders a row for a section field
  const HierarchyRow = ({
    level,
    title,
    fieldName,
    isEditable,
    hasChildren,
    isExpanded,
    onToggle,
    suffix,
    buttonHandler
  }) => {
    // Calculate indentation based on level
    const indentation = (level - 1) * 24;
    // Use defaultValue from state
    const displayValue = isEditable ? state[fieldName] : formatDisplayNumber(state[fieldName]);
    return (
      <Box borderBottom="1px solid" borderColor="gray.100">
        <Flex bgColor="#fff" p={2} justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" style={{ width: 'auto', flex: '0 0 auto' }}>
            {indentation > 0 && (
              <Box style={{ width: `${indentation}px`, minWidth: `${indentation}px`, flexShrink: 0 }} />
            )}
            {hasChildren && (
              <Box onClick={onToggle} cursor="pointer" mr={2}>
                {isExpanded ? <ChevronDownIcon boxSize={4} /> : <ChevronRightIcon boxSize={4} />}
              </Box>
            )}
            {buttonHandler ? (
              <Button
                size="sm"
                fontSize=".9rem"
                mr={1}
                variant="solid"
                style={{ width: '180px' }}
                textAlign="left"
                display="inline"
                cursor="pointer"
                onClick={buttonHandler}
                _hover={{ bg: 'blue.100' }}
              >
                {title}
              </Button>
            ) : (
              <FormLabel
                size="md"
                fontSize=".9rem"
                htmlFor={`input_${fieldName}`}
                style={{ width: '180px' }}
                textAlign="left"
                display="inline"
                m={0}
              >
                {title}
              </FormLabel>
            )}
          </Flex>
          <InputGroup flex="1" minWidth="150px">
            {suffix && (
              <InputRightElement
                pointerEvents="none"
                color="gray.600"
                background="gray.200"
                fontWeight={600}
                width="auto"
                minWidth="var(--input-height)"
                pl={1}
                pr={1}
                fontSize=".9rem"
                borderTopRightRadius={8}
                borderBottomRightRadius={8}
              >
                {suffix}
              </InputRightElement>
            )}
            <Input
              id={`input_${fieldName}`}
              defaultValue={displayValue}
              ref={getInputRef(fieldName)}
              onBlur={() => handleBlur(fieldName)}
              onKeyDown={(e) => handleKeyDown(e, fieldName)}
              isDisabled={!isEditable}
              fontSize="small"
            />
          </InputGroup>
        </Flex>
      </Box>
    );
  };

  // Helper for handling total clicks (for graphs)
  const handleCapexTotalClick = () => {
    const capexValue = parseFloat(state.totalCapexValue);
    if (capexValue && capexValue !== 0) {
      props.openCapexGraph();
    }
  };

  const handleNPVClick = () => {
    props.openNPVGraph();
  };

  const handleOpexTotalClick = () => {
    props.openOpexGraph();
  };

  // Helper for getting suffix and button handler for a field name
  const getSuffixForField = (fieldName) => {
    switch (fieldName) {
      case 'NPVValue':
      case 'additionalCapexValue':
      case 'totalCapexValue':
      case 'totalInstalledValue':
      case 'equipmentCapexValue':
      case 'installationCostsValue':
      case 'operatorSalaryValue':
      case 'operatorBenefitsValue':
      case 'laborOverheadsValue':
        return '$';
      case 'langFactorValue':
      case 'materialFactorValue':
      case 'locationFactorValue':
      case 'maintenanceFactorValue':
      case 'landRentFactorValue':
      case 'propertyTaxFactorValue':
        return '';
      case 'LCOPValue':
        return '$/T';
      case 'elecCostValue':
        return '$/MWh';
      case 'totalOpexValue':
      case 'totalOpexDisplayValue':
      case 'additionalOpexValue':
      case 'totalProductRevValue':
      case 'totalFeedstockCostValue':
      case 'utilityValue':
      case 'wasteDisposalValue':
      case 'laborValue':
      case 'envChargesValue':
      case 'maintenanceValue':
      case 'landRentandPropertyTaxValue':
      case 'additionalRevenueAnnualValue':
        return '$/Annum';
      case 'interestRateValue':
        return '0.0';
      case 'operatorCountValue':
        return 'people';
      case 'envChargesFactorValue':
      case 'benefitsPercentValue':
        return '%';
      default:
        return undefined;
    }
  };

  const getHandlerForField = (fieldName) => {
    switch (fieldName) {
      case 'NPVValue':
        return handleNPVClick;
      case 'totalInstalledValue':
        return handleCapexTotalClick;
      case 'totalOpexDisplayValue':
        return handleOpexTotalClick;
      default:
        return undefined;
    }
  };

  // getEconArrays and updateEconArrays remain unchanged from before.
  const getEconArrays = () => {
    return [updateEconVars, props.graph, props.solved_Dictionary, props.econVars];
  };

  const updateEconArrays = (graph, solved_Dictionary, econVars) => {
    let allEquipment = graph.getElements();
    let opexSum = 0;
    let capexSum = 0;
    let feedCostSum = 0;
    let productCostSum = 0;
    let streamEP = 0;
    let equipNameArray = [];
    let capexArrayOG = [];
    let opexArrayOG = [];

    for (let i = 0; i < allEquipment.length; i++) {
      if (allEquipment[i].prop("isUnit") === "yes") {
        const unitType = allEquipment[i].attributes['equipment_Properties']['unitType'].toLowerCase();
        if (unitType === 'feed') {
          if (allEquipment[i].attributes['equipment_Economics'] && allEquipment[i].attributes['equipment_Economics']['Cost']) {
            streamEP = props.calculateFeedEP(graph, allEquipment[i]);
            feedCostSum += streamEP;
          }
        } else if (unitType === 'product') {
          if (allEquipment[i].attributes['equipment_Economics'] && allEquipment[i].attributes['equipment_Economics']['Cost']) {
            let prodCost = parseFloat(allEquipment[i].attributes['equipment_Economics']['Cost']['Value']);
            let inputStream = graph.getConnectedLinks(allEquipment[i]);
            let totalFlow = inputStream[0].attributes['complete_Stream_Properties']['totalFlow'];
            let streamEP = totalFlow * prodCost;
            productCostSum += streamEP;
          }
        } else {
          if (allEquipment[i].attributes['equipment_Economics']) {
            opexSum += parseFloat(allEquipment[i].attributes['equipment_Economics']['Opex']['Value']);
            capexSum += parseFloat(allEquipment[i].attributes['equipment_Economics']['Capex']['Value']);
            let equipName = allEquipment[i].attributes.attrs.shapename.innerHTML.replace(/<p>|<\/p>/g, '');
            let capexValue = allEquipment[i].attributes['equipment_Economics']['Capex']['Value'];
            let opexValue = allEquipment[i].attributes['equipment_Economics']['Opex']['Value'];
            equipNameArray.push(equipName);
            capexArrayOG.push(capexValue);
            opexArrayOG.push(opexValue);
          }
        }
      }
    }
    let equipArray = [equipNameArray, capexArrayOG, opexArrayOG];
    let keyFeedNo = solved_Dictionary['streams'][Number(econVars.keyFeedStream)]['totalFlow'];
    let streamEPNorm;
    if (keyFeedNo !== null) {
      streamEPNorm = streamEP / keyFeedNo;
    } else {
      toast.error("Please specify all total flow values to calculate Economic Potential.");
    }
    return [equipArray, opexSum, capexSum, streamEPNorm, feedCostSum, productCostSum];
  };

  // Render the component using the PopupLayout and hierarchical sections
  const savedPosition = localStorage.getItem(POPUP_POSITION_KEY);
  const initialPosition = savedPosition ? JSON.parse(savedPosition) : { x: 0, y: 0 };

  return (
    <PopupLayout
      id={props.Id}
      closeFunction={props.closeFunction}
      ref={ref}
      pageX={initialPosition.x}
      pageY={initialPosition.y}
      title="Analysis Results"
      componentRef={componentRef}
      maxHeight="80vh"
      minHeight="600px"
      minWidth="450px"
    >
      {hierarchicalSections.map(section => (
        <Box key={section.id} mb={2}>
          {section.fields.length > 0 && (
            <HierarchyRow
              level={1}
              title={section.title}
              fieldName={section.fields[0]}
              isEditable={allowedVars.includes(section.fields[0])}
              hasChildren={section.children && section.children.length > 0}
              isExpanded={expandState[section.id].isOpen}
              onToggle={() => toggleLevel1(section.id)}
              suffix={getSuffixForField(section.fields[0])}
              buttonHandler={getHandlerForField(section.fields[0])}
            />
          )}
          {(section.children || section.fields.length > 1) && (
            <Collapse in={expandState[section.id].isOpen}>
              {section.fields.slice(1).map(fieldName => (
                <HierarchyRow
                  key={fieldName}
                  level={1}
                  title={fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/Value$/, '').replace(/([A-Z])/g, ' $1')}
                  fieldName={fieldName}
                  isEditable={allowedVars.includes(fieldName)}
                  hasChildren={false}
                  suffix={getSuffixForField(fieldName)}
                  buttonHandler={getHandlerForField(fieldName)}
                />
              ))}
              {section.children && section.children.map(subsection => (
                <Box key={subsection.id}>
                  <HierarchyRow
                    level={2}
                    title={subsection.title}
                    fieldName={subsection.fields[0] || subsection.valueField || ''}
                    isEditable={allowedVars.includes(subsection.fields[0] || subsection.valueField || '')}
                    hasChildren={subsection.children && subsection.children.length > 0}
                    isExpanded={expandState[section.id].children[subsection.id].isOpen}
                    onToggle={() => toggleLevel2(section.id, subsection.id)}
                    suffix={getSuffixForField(subsection.fields[0] || subsection.valueField || '')}
                    buttonHandler={getHandlerForField(subsection.fields[0] || subsection.valueField || '')}
                  />
                  {(subsection.children || subsection.fields.length > 1) && (
                    <Collapse in={expandState[section.id].children[subsection.id].isOpen}>
                      {subsection.fields.slice(1).map(fieldName => (
                        <HierarchyRow
                          key={fieldName}
                          level={2}
                          title={fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/Value$/, '').replace(/([A-Z])/g, ' $1')}
                          fieldName={fieldName}
                          isEditable={allowedVars.includes(fieldName)}
                          hasChildren={false}
                          suffix={getSuffixForField(fieldName)}
                          buttonHandler={getHandlerForField(fieldName)}
                        />
                      ))}
                      {subsection.children && subsection.children.map(item => (
                        <Box key={item.id}>
                          <HierarchyRow
                            level={3}
                            title={item.title}
                            fieldName={item.fields[0] || ''}
                            isEditable={allowedVars.includes(item.fields[0] || '')}
                            hasChildren={false}
                            suffix={getSuffixForField(item.fields[0] || '')}
                            buttonHandler={getHandlerForField(item.fields[0] || '')}
                          />
                        </Box>
                      ))}
                    </Collapse>
                  )}
                </Box>
              ))}
            </Collapse>
          )}
        </Box>
      ))}

    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}> 
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Unsaved Changes</AlertDialogHeader>
            <AlertDialogBody>Save changes before closing?</AlertDialogBody>
            <AlertDialogFooter>
            <Button onClick={onClose}>Cancel</Button>

                <Button
                onClick={async () => {
                    const hasErrors = Object.entries(mapping).some(([stateKey]) => {
                    const value = state[stateKey];
                    if (!allowedVars.includes(stateKey)) {
                        if (value !== '' && isNaN(parseFloat(value))) {
                        toast.error(`${stateKey.replace(/Value$/, '')} must be a valid number`);
                        return true;
                        }
                        return false;
                    }
                    if (validations[stateKey]) {
                        const numValue = parseFloat(value);
                        if (isNaN(numValue) || numValue < validations[stateKey].min) {
                        toast.error(validations[stateKey].message);
                        return true;
                        }
                    }
                    return false;
                    });

                    if (hasErrors) {
                    onClose();
                    return;
                    }

                    updateEconVars();
                    await doReq();
                    props.closeFunction();
                }}
                ml={3}
                >
                Save
                </Button>

                <Button
                onClick={() => {
                    setState(prev => {
                    const newState = { ...prev };
                    Object.entries(dirtyFields).forEach(([field, isDirty]) => {
                        if (isDirty && mapping[field]) {
                        newState[field] = props.econVars[mapping[field]] || "";
                        }
                    });
                    return newState;
                    });
                    setDirtyFields(Object.fromEntries(Object.keys(dirtyFields).map(key => [key, false])));
                    onClose();
                    props.closeFunction();
                }}
                ml={3}
                >
                Discard
                </Button>

            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PopupLayout>
  );
});

export default PropertyPopup;
